import { Heart, Instagram } from 'lucide-react';
import { Link } from 'react-router-dom';

import React from 'react';

import { useTranslation } from '@splitfiesta/i18n';

import { StoreButtons } from '@/components/common/StoreButtons';
import { XIcon } from '@/components/ui/XIcon';

interface FooterProps {
  variant?: 'default' | 'minimal';
}

export function Footer({ variant = 'default' }: FooterProps) {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  if (variant === 'minimal') {
    return (
      <footer className="mt-auto py-6 px-4" data-testid="footer-minimal">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {currentYear} SplitFiesta. {t('common:footer.madeWith')}{' '}
            <Heart
              className="inline-block w-4 h-4 text-red-500"
              fill="currentColor"
            />{' '}
            {t('common:footer.byTeam')}
          </p>
        </div>
      </footer>
    );
  }

  return (
    <footer
      className="mt-auto bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              SplitFiesta
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('common:footer.tagline')}
            </p>
            <div className="flex space-x-4" data-testid="footer-social-links">
              <a
                href="https://instagram.com/splitfiesta"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://x.com/splitfiesta"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
                aria-label="X"
              >
                <XIcon className="w-5 h-5" />
              </a>
            </div>
            <StoreButtons className="mt-5" />
          </div>

          {/* Quick Links */}
          <div data-testid="footer-links">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
              {t('common:footer.quickLinks')}
            </h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/about"
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  {t('common:footer.about')}
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  {t('common:footer.privacy')}
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  {t('common:footer.terms')}
                </Link>
              </li>
              <li>
                {/* Google Data-safety: the deletion resource must be
                    discoverable — linked here next to the legal pages. */}
                <Link
                  to="/account/deletion"
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  {t('common:footer.accountDeletion')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div data-testid="footer-support-links">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
              {t('common:footer.support')}
            </h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/faq"
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  {t('common:footer.faq')}
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  {t('common:footer.contact')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div
          className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700"
          data-testid="footer-copyright"
        >
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            © {currentYear} SplitFiesta. {t('common:footer.rights')} •{' '}
            {t('common:footer.madeWith')}{' '}
            <Heart
              className="inline-block w-4 h-4 text-red-500"
              fill="currentColor"
            />{' '}
            {t('common:footer.byTeam')}
          </p>
        </div>
      </div>
    </footer>
  );
}
